import { useState } from 'react';
import { CurrencyCircleDollar, Info } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import HeruCashExplainerModal from './HeruCashExplainerModal';

/**
 * La promesa del experimento, ANTES de vincular.
 *
 * EL COPY ES EL TRATAMIENTO, no decoración: es lo que el experimento mide.
 * Cambiarlo hace incomparables los datos de antes y después, así que cada
 * cambio exige arrancar FASE NUEVA en GrowthBook para tener un corte de fecha
 * limpio — pero SIN re-randomizar el tráfico, porque el backend congela el
 * brazo por user_id y rotar el seed desalinearía las promesas ya persistidas
 * de lo que reporta la exposición.
 *
 * Este copy incorpora el plazo acordado con Ops y Karla (ver `manana`).
 *
 * PURO A PROPÓSITO, Y VIVE AQUÍ POR ESO. El monto llega ya resuelto —del hook
 * en el flujo legacy, del `ctx` en onboarding v3— y este componente no decide
 * nada, sólo pinta. Está en components/molecules y no dentro del feature porque
 * las pantallas de v3 son zona pura por contrato y sólo pueden importar de esa
 * allowlist: es lo que deja que los dos flujos compartan UN copy en vez de
 * duplicarlo y verlo divergir a media prueba.
 */

/**
 * Márgenes negativos que cancelan el padding de cada tarjeta que hospeda el
 * banner, para que la franja llegue a los bordes laterales.
 *
 * No hay un valor único porque las tarjetas de vinculación NO comparten
 * padding, y ese es justo el detalle que hacía que el banner se viera pegado
 * encima en vez de parte del componente. Cada sitio de montaje declara el suyo;
 * si alguien cambia el padding de una tarjeta, aquí está el lugar a ajustar.
 *
 * EL BLEED ES TOTAL POR LOS TRES LADOS: cada `-mt` cancela EXACTO el padding
 * superior de su tarjeta, igual que los `-mx` cancelan el lateral.
 *
 * Antes se dejaban 0.5rem de aire arriba a propósito, para que la franja no se
 * sintiera apretada contra el marco. Se quitó: las tarjetas que la hospedan son
 * `rounded-xl overflow-hidden`, así que ese medio rem dejaba ver las esquinas
 * redondeadas en blanco a los lados de la franja y el conjunto se leía
 * despegado del contenedor — justo el defecto que el bleed existe para evitar.
 * Para devolver el aire, sumarle 2 a cada `-mt` (y a los `md:`/`lg:`).
 */
export const BLEED = {
	/** onboarding v3 · LinkingCard (p-4 md:px-6 md:py-8) */
	v3: '-mx-4 -mt-4 md:-mx-6 md:-mt-8',
	/** legacy paso 1 · selección de método (px-2 md:px-6 py-5 md:py-8) */
	legacyStep1: '-mx-2 -mt-5 md:-mx-6 md:-mt-8',
	/** legacy paso 2 · credenciales (px-4 md:px-6 py-4 md:py-8) */
	legacyStep2: '-mx-4 -mt-4 md:-mx-6 md:-mt-8',
	/**
	 * Modal de e.firma. Cancela DOS paddings: el del Modal (p-4 lg:p-5) y el del
	 * wrapper interno (pt-6 px-2).
	 *
	 * SIN `rounded-t-3xl` aunque la franja vuelva a tocar la esquina: el radio lo
	 * pone el contenedor. Las dos ramas del Modal recortan por su cuenta
	 * (`rounded-3xl` + `overflow-y-auto` en escritorio, el DrawerContent en
	 * mobile), así que un radio propio sólo podría desalinearse del de arriba.
	 *
	 * `md:pr-14` —y no `pr-14`— deja hueco al botón de cerrar. El breakpoint
	 * importa por dos razones que se cancelaban entre sí: el `X` en `absolute
	 * top-3 right-3` sólo existe en la rama de ESCRITORIO del Modal (≥768px), y
	 * un `pr-14` sin prefijo lo pisa `md:px-6`, que Tailwind emite después.
	 */
	efirmaModal: '-mx-6 -mt-10 lg:-mx-7 lg:-mt-11 md:pr-14',
} as const;

/**
 * El plazo del copy: mañana, sin hora.
 *
 * ES UN EMPUJÓN, NO UNA PUERTA — y está decidido así con Ops y Karla.
 * `linking_reward` no tiene columna de expiración a propósito: quien vincule
 * pasada la fecha cobra igual, y a quien no vincule se le libera su heru-cash
 * por promoción dentro de la campaña de estos tres meses. Nadie queda peor por
 * creerle a la fecha; el peor caso es que alguien se apure.
 *
 * Por eso NO hay que "arreglarlo" agregando expiración en el backend: eso
 * convertiría una promesa que hoy siempre se cumple en una que a veces no, que
 * es justo lo contrario de lo que el equipo decidió.
 *
 * Se recalcula en cada render: siempre dice mañana, sin fechas que mantener.
 */
const manana = (): string =>
	new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString('es-MX', {
		day: 'numeric',
		month: 'long',
	});

const LinkingRewardBanner = ({
	// number desde el hook, string desde el ctx del motor de onboarding (FlowCtx
	// es boolean|string). Sólo se interpola, nunca se opera: por eso acepta los
	// dos en vez de obligar a una conversión en zona pura.
	rewardAmount,
	bleed,
	onExplainerOpen,
}: {
	rewardAmount: number | string;
	/**
	 * Clases de margen negativo que cancelan el padding del contenedor. Se pasa
	 * SÓLO cuando el banner es el primer hijo de una tarjeta (ver `BLEED`); el
	 * overflow de la tarjeta recorta las esquinas.
	 *
	 * Sin esta prop la franja nace a lo ancho de su contenedor, que es lo
	 * correcto donde no hay padding que cancelar. Pasarla en el sitio equivocado
	 * saca el banner del viewport.
	 */
	bleed?: string;
	/**
	 * Handler de apertura del explicador. SU PRESENCIA ES EL INTERRUPTOR: con él
	 * la franja entera es tappable y abre `HeruCashExplainerModal`; sin él queda
	 * informativa, como nació.
	 *
	 * Es una sola prop y no dos porque las dos condiciones son la misma: el
	 * explicador sólo cabe donde hay superficie para un modal —las pantallas de
	 * vinculación, no el modal de e.firma, donde sería un modal sobre otro— y ahí
	 * mismo vive quien puede reportar el hecho. La franja es pura: recibe el
	 * handler, nunca importa tracking (es lo que la deja montarse en las
	 * pantallas de v3).
	 */
	onExplainerOpen?: () => void;
}) => {
	const [explainerOpen, setExplainerOpen] = useState(false);

	// Sin borde propio salvo el inferior, que es el que lo separa del contenido:
	// un recuadro completo en una franja a sangre vuelve a leerse como algo
	// pegado encima en vez de parte del componente.
	const strip = (
		<>
			{/* EL MISMO ÍCONO QUE LA TOPBAR Y EL POPUP DE REFERIDOS
			    (CurrencyCircleDollar duotone, 20px): heru-cash es UN producto y sus
			    tres puertas tienen que leerse como tal. Sólo cambia el color, que
			    aquí lo manda el verde de la franja. */}
			<CurrencyCircleDollar
				weight='duotone'
				size={20}
				className='mt-0.5 min-w-[20px] text-emerald-600'
			/>
			<p className='text-left text-sm leading-snug text-emerald-900'>
				Vincula antes del <b>{manana()}</b> y gana{' '}
				<b>${rewardAmount} de heru-cash</b> para tu próxima renovación o compra.
			</p>
		</>
	);
	// EL BLEED VA EN EL WRAPPER, NO EN LA FRANJA. Los márgenes negativos hacen
	// crecer una caja de ancho AUTO, pero no una de `w-full`: width:100% se mide
	// contra el contenedor e ignora los `-mx`, así que la franja se recorría a la
	// izquierda y le faltaba justo esa misma medida a la derecha. El wrapper
	// (auto) se estira con el bleed y la franja llena el wrapper ya estirado.
	const stripClassName = `flex w-full items-start gap-3 border-b
		 border-emerald-200/70 px-4 py-3 bg-gradient-to-r from-emerald-50
		 via-emerald-50/70 to-teal-50/40 md:px-6 md:py-4`;

	const openExplainer = () => {
		setExplainerOpen(true);
		onExplainerOpen?.();
	};

	return (
		<div className={cn('relative z-10', bleed)}>
			{onExplainerOpen ? (
				/* La franja ENTERA es el target, no el ícono: en el heatmap los clics
				   caen sobre las monedas pero también sobre el copy, y 20px de ícono
				   no son un target táctil. `type='button'` es obligatorio — el banner
				   se monta dentro del <form> de credenciales en más de una pantalla. */
				<button
					type='button'
					onClick={openExplainer}
					aria-haspopup='dialog'
					className={stripClassName}>
					{strip}
					<span className='sr-only'>Qué es el heru-cash</span>
					<Info
						aria-hidden
						size={16}
						className='mt-0.5 min-w-[16px] text-emerald-600/70'
					/>
				</button>
			) : (
				<div className={stripClassName}>{strip}</div>
			)}
			{onExplainerOpen && (
				<HeruCashExplainerModal
					open={explainerOpen}
					onClose={() => setExplainerOpen(false)}
					rewardAmount={rewardAmount}
				/>
			)}
		</div>
	);
};

export default LinkingRewardBanner;
